import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import GroveLaunchWaitlistModal from "@/components/home/GroveLaunchWaitlistModal";
import GroveLaunchTermsModal from "@/components/home/GroveLaunchTermsModal";
import { GTA6_RELEASE_TS, GTA6_STANDARD_ID, GTA6_ULTIMATE_ID, GTA6_EDITIONS } from "@/lib/gta6";

import cover from "@/assets/gta6/official_cover_art.jpg.asset.json";
import jl1 from "@/assets/gta6/jason_and_lucia_01.jpg.asset.json";
import jl2 from "@/assets/gta6/jason_and_lucia_02.jpg.asset.json";
import jl3 from "@/assets/gta6/jason_and_lucia_03.jpg.asset.json";
import motel from "@/assets/gta6/jason_and_lucia_motel.jpg.asset.json";

const MAX_SPOTS = 50;

const SLIDES = [
  { src: jl1.url, alt: "Jason and Lucia — GTA VI key art" },
  { src: motel.url, alt: "Jason and Lucia at the Vice City motel" },
  { src: jl2.url, alt: "Jason and Lucia getaway — GTA VI" },
  { src: jl3.url, alt: "Jason and Lucia neon night — GTA VI" },
  { src: cover.url, alt: "GTA VI official cover art" },
];

const PINK = "#FF3D8A";
const CYAN = "#19E0FF";
const NIGHT = "#0A0420";

function useCountdown(target: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
}
const pad = (n: number, l = 2) => String(n).padStart(l, "0");

const HeroSection = () => {
  const [openWaitlist, setOpenWaitlist] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const { days, hours, mins, secs } = useCountdown(GTA6_RELEASE_TS);

  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => setSlideIdx((i) => (i + 1) % SLIDES.length), 7000);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const { data: claimed = 0 } = useQuery({
    queryKey: ["grove-launch-count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("grove_launch_count");
      if (error) throw error;
      return Number(data) || 0;
    },
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });
  const remaining = Math.max(0, MAX_SPOTS - claimed);
  const pct = Math.min(100, Math.round((claimed / MAX_SPOTS) * 100));
  const isFull = claimed >= MAX_SPOTS;

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  const tiles = [
    { val: pad(days, 3), label: "Days" },
    { val: pad(hours), label: "Hours" },
    { val: pad(mins), label: "Mins" },
    { val: pad(secs), label: "Secs" },
  ];

  const marqueeText = `Exclusive Pre-order Live  •  Grove Launch System  •  ${claimed}/${MAX_SPOTS} Cashback Spots Claimed  •  Launching 19 . 11 . 2026 IST  •  20% Cashback up to ₹1,200  •  `;

  return (
    <>
      <section
        ref={sectionRef}
        className="relative w-full overflow-hidden"
        style={{ background: NIGHT }}
      >
        {/* Top Marquee Ticker */}
        <div
          className="relative z-30 overflow-hidden py-1.5 border-y"
          style={{ background: PINK, borderColor: "rgba(255,255,255,0.2)" }}
        >
          <div className="marquee-track flex whitespace-nowrap">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="inline-block min-w-max px-4 text-[10px] font-black uppercase tracking-[0.25em]"
                style={{ color: NIGHT }}
              >
                {marqueeText}
              </span>
            ))}
          </div>
        </div>

        <div className="relative min-h-[calc(100vh-2rem)]">
          {/* Slideshow */}
          <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
            <AnimatePresence mode="sync">
              <motion.img
                key={slideIdx}
                src={SLIDES[slideIdx].src}
                alt={SLIDES[slideIdx].alt}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1.14 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 1.4 }, scale: { duration: 9, ease: "linear" } }}
                className="absolute inset-0 h-full w-full object-cover"
                width={1920}
                height={1080}
                loading={slideIdx === 0 ? "eager" : "lazy"}
                // @ts-expect-error fetchpriority is valid HTML
                fetchpriority={slideIdx === 0 ? "high" : "auto"}
                decoding="async"
              />
            </AnimatePresence>
          </motion.div>

          {/* Cinematic gradient overlays */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: `linear-gradient(180deg, ${NIGHT}cc 0%, ${NIGHT}55 30%, ${NIGHT}99 65%, ${NIGHT} 100%)`,
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 mix-blend-screen opacity-40"
            style={{
              background: `radial-gradient(ellipse at 20% 100%, ${PINK}55 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, ${CYAN}44 0%, transparent 60%)`,
            }}
          />
          {/* Scanlines */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)",
            }}
          />
          {/* Grain */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
            }}
          />

          <motion.div
            style={{ opacity: contentOpacity }}
            className="relative z-10 mx-auto flex min-h-[calc(100vh-2rem)] max-w-lg flex-col justify-between px-6 pt-10 pb-12 sm:max-w-xl sm:px-8 sm:pt-14 sm:pb-16"
          >
            {/* Logo lockup */}
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div
                className="mb-4 inline-block rounded-full border px-3 py-1 backdrop-blur-md"
                style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}
              >
                <p
                  className="text-[9px] font-black uppercase tracking-[0.3em]"
                  style={{ color: CYAN }}
                >
                  Grove × Rockstar Games Drop
                </p>
              </div>
              <h1
                className="font-display font-black italic uppercase leading-[0.85] tracking-tighter text-white"
                style={{ fontSize: "clamp(3.75rem, 14vw, 6.5rem)" }}
              >
                GTA{" "}
                <span
                  style={{
                    color: PINK,
                    filter: `drop-shadow(0 0 18px ${PINK}99)`,
                  }}
                >
                  VI
                </span>
              </h1>
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.25em] text-white/60 sm:text-sm">
                Vice City · Leonida · India Exclusive
              </p>
            </motion.div>

            {/* Tactical HUD Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              className="relative my-8"
            >
              {/* HUD brackets */}
              <span className="absolute -left-2 -top-2 h-6 w-6 border-l border-t" style={{ borderColor: `${CYAN}80` }} />
              <span className="absolute -right-2 -top-2 h-6 w-6 border-r border-t" style={{ borderColor: `${CYAN}80` }} />
              <span className="absolute -left-2 -bottom-2 h-6 w-6 border-l border-b" style={{ borderColor: `${CYAN}80` }} />
              <span className="absolute -right-2 -bottom-2 h-6 w-6 border-r border-b" style={{ borderColor: `${CYAN}80` }} />

              <div
                className="rounded-sm border p-5 backdrop-blur-xl sm:p-6"
                style={{ background: "rgba(0,0,0,0.62)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                <div className="mb-5 flex items-end justify-between">
                  <div>
                    <p
                      className="mb-1 text-[10px] font-bold uppercase tracking-[0.25em]"
                      style={{ color: PINK }}
                    >
                      Release Date
                    </p>
                    <p className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
                      Nov 19, 2026
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/40">
                      Status
                    </p>
                    <p
                      className="mt-0.5 flex items-center justify-end gap-1.5 text-[10px] font-black uppercase tracking-widest"
                      style={{ color: CYAN }}
                    >
                      <span
                        className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                        style={{ background: CYAN, boxShadow: `0 0 8px ${CYAN}` }}
                      />
                      Incoming
                    </p>
                  </div>
                </div>

                {/* Countdown */}
                <div className="mb-6 grid grid-cols-4 gap-2">
                  {tiles.map((t) => (
                    <div
                      key={t.label}
                      className="rounded border p-2 text-center"
                      style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.06)" }}
                    >
                      <span className="block font-mono text-lg font-bold tabular-nums text-white sm:text-xl">
                        {t.val}
                      </span>
                      <span
                        className="text-[8px] font-bold uppercase tracking-tight"
                        style={{ color: CYAN }}
                      >
                        {t.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Cashback tracker */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
                    <span className="text-white/50">Cashback Heist</span>
                    <span style={{ color: PINK }} className="tabular-nums">
                      {claimed}/{MAX_SPOTS} Enrolled {!isFull && <span className="text-white/40">· {remaining} left</span>}
                    </span>
                  </div>
                  <div className="h-1 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.1)" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 1.2, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{
                        background: `linear-gradient(90deg, ${CYAN}, ${PINK})`,
                        boxShadow: `0 0 10px ${PINK}aa`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Interaction Zone */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6 }}
              className="space-y-3"
            >
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to={`/product/${GTA6_STANDARD_ID}`}
                  className="group rounded-xl border py-3 px-3 transition-all hover:-translate-y-0.5"
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.12)" }}
                >
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
                    Standard
                  </p>
                  <p className="mt-0.5 font-display text-base font-bold text-white sm:text-lg">
                    ₹{GTA6_EDITIONS.standard.price.toLocaleString("en-IN")}
                  </p>
                </Link>
                <Link
                  to={`/product/${GTA6_ULTIMATE_ID}`}
                  className="group rounded-xl border py-3 px-3 transition-all hover:-translate-y-0.5"
                  style={{
                    background: `${CYAN}0d`,
                    borderColor: `${CYAN}55`,
                    boxShadow: `0 0 20px ${CYAN}22`,
                  }}
                >
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: CYAN }}>
                    Ultimate
                  </p>
                  <p className="mt-0.5 font-display text-base font-bold text-white sm:text-lg">
                    ₹{GTA6_EDITIONS.ultimate.price.toLocaleString("en-IN")}
                  </p>
                </Link>
              </div>

              <Link
                to={`/product/${GTA6_STANDARD_ID}`}
                className="flex w-full items-center justify-center gap-2 rounded-xl py-4 text-xs font-black uppercase tracking-[0.25em] transition-transform hover:-translate-y-0.5"
                style={{
                  background: PINK,
                  color: NIGHT,
                  boxShadow: `0 8px 30px ${PINK}55, 0 0 0 1px rgba(255,255,255,0.1) inset`,
                }}
              >
                Pre-order GTA VI
              </Link>

              <div className="flex items-center justify-center gap-4 pt-1">
                <button
                  onClick={() => setOpenWaitlist(true)}
                  disabled={isFull}
                  className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 underline underline-offset-4 transition-colors hover:text-white disabled:opacity-50"
                  style={{ textDecorationColor: CYAN }}
                >
                  {isFull ? "Waitlist Full" : "Join Cashback Waitlist"}
                </button>
                <span className="text-white/20">·</span>
                <button
                  onClick={() => setOpenTerms(true)}
                  className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 underline underline-offset-4 transition-colors hover:text-white"
                  style={{ textDecorationColor: PINK }}
                >
                  Read the Rules
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <GroveLaunchWaitlistModal open={openWaitlist} onOpenChange={setOpenWaitlist} initialFull={isFull} />
      <GroveLaunchTermsModal open={openTerms} onOpenChange={setOpenTerms} />
    </>
  );
};

export default HeroSection;
